import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Select,
  Space,
  Table,
  message,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Boat, BoatNote, ShippableGINLine } from "../../api-models";
import { BoatFormModal } from "../../components/BoatFormModal";
import { DocLink } from "../../components/DocLink";
import { DocumentPrintSheet } from "../../components/DocumentPrintSheet";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { PageHeader } from "../../components/PageHeader";
import { QtyInput } from "../../components/QtyInput";
import { SiteLabel } from "../../components/SiteLabel";
import { SiteSelect } from "../../components/SiteSelect";
import { useUser } from "../../context/UserContext";
import { DT_FORMATS } from "../../helpers/constants";
import { fmtSite } from "../../helpers/format";
import { hasAnyGrant, hasPermission } from "../../helpers/permissions";
import { useBoatsQuery, useSitesQuery } from "../../helpers/queries";
import { useApi } from "../../helpers/useApi";

/** A shippable line the user has ticked, with the quantity they want on this boat. */
interface DraftLine extends ShippableGINLine {
  qty: number;
}

export default function BoatNoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, loading } = useApi();
  const { user } = useUser();
  const [form] = Form.useForm();

  const isNew = id === "new";
  const canWrite = hasAnyGrant(user, "BoatNotes.Write");
  // Boats.Write is global-only on the server (boats.go), so mirror that: a site grant
  // would show buttons every save then rejects.
  const canWriteBoats = hasPermission(user, "Boats.Write");
  const { data: sites = [] } = useSitesQuery();
  const { data: boats = [] } = useBoatsQuery();

  // The boat is picked from the register; the two buttons beside the picker open the same
  // form the Boats master uses, so a vessel missing from the list (or with a stale phone
  // number) can be sorted out without leaving the half-built manifest behind.
  const boatId = Form.useWatch<number | undefined>("boatId", form);
  const selectedBoat = boats.find((b) => b.id === boatId);
  const [boatModal, setBoatModal] = useState<{
    open: boolean;
    boat: Boat | null;
  }>({ open: false, boat: null });

  const [siteId, setSiteId] = useState<number | undefined>();
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [shippableLoaded, setShippableLoaded] = useState(false);
  const [bn, setBn] = useState<BoatNote | null>(null);

  function load() {
    request<BoatNote>({ url: `/boat-notes/${id}` }).then(setBn);
  }

  useEffect(() => {
    if (!isNew) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Picking the site is what fills the manifest builder: everything dispatched to that
  // site and not already claimed by another boat note is a candidate.
  useEffect(() => {
    if (!isNew || !siteId) {
      setLines([]);
      setSelected([]);
      return;
    }
    setShippableLoaded(false);
    request<ShippableGINLine[]>({
      url: "/boat-notes/shippable",
      params: { siteId },
    }).then((rows) => {
      setLines((rows ?? []).map((r) => ({ ...r, qty: r.shippableQty })));
      setSelected([]);
      setShippableLoaded(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, siteId]);

  async function submit() {
    const values = await form.validateFields();
    const items = lines
      .filter((l) => selected.includes(l.ginLineId) && l.qty > 0)
      .map((l) => ({ ginLineId: l.ginLineId, qty: l.qty }));
    if (items.length === 0) {
      message.error("Select at least one line to load onto the boat.");
      return;
    }
    const created = await request<BoatNote>({
      method: "POST",
      url: "/boat-notes",
      data: {
        siteId: values.siteId,
        boatId: values.boatId,
        submissionDate: (values.submissionDate as Dayjs | undefined)?.format(
          "YYYY-MM-DD",
        ),
        notes: values.notes,
        items,
      },
    });
    message.success("Boat note created.");
    navigate(`/boat-notes/${created.id}`, { replace: true });
  }

  async function act(action: "issue" | "cancel") {
    await request({ method: "POST", url: `/boat-notes/${id}/${action}` });
    message.success(
      action === "issue" ? "Boat note issued." : "Boat note cancelled.",
    );
    load();
  }

  if (isNew) {
    return (
      <div>
        <PageHeader
          title="New Boat Note"
          extra={
            <Button
              type="primary"
              onClick={submit}
              loading={loading}
              disabled={!canWrite}
              data-tour="boat-note-create"
            >
              Create boat note
            </Button>
          }
        />
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form
            form={form}
            layout="vertical"
            initialValues={{ submissionDate: dayjs() }}
          >
            <Form.Item
              name="siteId"
              label="Destination site"
              rules={[
                {
                  required: true,
                  message: "Select the site this boat is bound for.",
                },
              ]}
            >
              <SiteSelect
                data-tour="boat-note-site"
                showSearch
                optionFilterProp="label"
                placeholder="Select a site"
                onChange={(v) => setSiteId(v)}
                options={sites.map((s) => ({ value: s.id, label: fmtSite(s) }))}
              />
            </Form.Item>
            <Form.Item label="Boat" required>
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item
                  name="boatId"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: "Pick the boat carrying this load.",
                    },
                  ]}
                >
                  <Select
                    data-tour="boat-note-boat"
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select a boat"
                    options={boats.map((b) => ({
                      value: b.id,
                      label: b.contact ? `${b.name} — ${b.contact}` : b.name,
                    }))}
                  />
                </Form.Item>
                {canWriteBoats && (
                  <>
                    <Button
                      disabled={!selectedBoat}
                      onClick={() =>
                        setBoatModal({ open: true, boat: selectedBoat ?? null })
                      }
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => setBoatModal({ open: true, boat: null })}
                    >
                      New boat
                    </Button>
                  </>
                )}
              </Space.Compact>
            </Form.Item>
            <Form.Item name="submissionDate" label="Date of submission">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        </Card>

        {siteId && shippableLoaded && lines.length === 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Nothing is waiting to be loaded for this site"
            description="A boat note carries goods that have already been issued. Dispatch an issue note (GIN) to this site first, or check whether the outstanding lines are already on another boat note."
            action={
              <Button onClick={() => navigate("/issues")}>Go to issues</Button>
            }
          />
        )}

        <Card size="small" title="Load" data-tour="boat-note-lines">
          <Table<DraftLine>
            rowKey="ginLineId"
            dataSource={lines}
            pagination={false}
            locale={{
              emptyText: siteId
                ? "Nothing outstanding for this site."
                : "Select a destination site first.",
            }}
            rowSelection={{
              selectedRowKeys: selected,
              onChange: (keys) => setSelected(keys as number[]),
            }}
            columns={[
              {
                title: "Issue Note",
                render: (_, r) => (
                  <DocLink docType="GIN" id={r.ginId} docNo={r.ginDocNo} />
                ),
              },
              {
                title: "Request",
                render: (_, r) => (
                  <DocLink docType="MRQ" id={r.mrqId} docNo={r.mrqDocNo} />
                ),
              },
              { title: "Code", dataIndex: "itemCode" },
              { title: "Item", dataIndex: "itemName" },
              {
                title: "Outstanding",
                render: (_, r) => `${r.shippableQty} ${r.unit ?? ""}`,
              },
              {
                title: "Load qty",
                render: (_, r) => (
                  <QtyInput
                    value={r.qty}
                    unit={r.unit}
                    max={r.shippableQty}
                    onChange={(v) =>
                      setLines((prev) =>
                        prev.map((l) =>
                          l.ginLineId === r.ginLineId
                            ? { ...l, qty: v ?? 0 }
                            : l,
                        ),
                      )
                    }
                  />
                ),
              },
            ]}
          />
        </Card>

        <BoatFormModal
          open={boatModal.open}
          boat={boatModal.boat}
          onCancel={() => setBoatModal({ open: false, boat: null })}
          onSaved={(boat) => {
            // A boat retired in the middle of building a manifest can no longer carry it,
            // so the selection is dropped rather than left pointing at a retired vessel.
            form.setFieldValue("boatId", boat.active ? boat.id : undefined);
            setBoatModal({ open: false, boat: null });
          }}
        />
      </div>
    );
  }

  if (!bn) return null;

  const isDraft = bn.status === "DRAFT";
  const lineRows = bn.lines ?? [];

  return (
    <div>
      <PageHeader
        title={bn.docNo}
        subtitle={
          <Space>
            <SiteLabel site={bn.site} />
            <span data-tour="doc-status">
              <DocumentStatusTag status={bn.status} />
            </span>
          </Space>
        }
        extra={
          <Space>
            {canWrite && isDraft && (
              <Button
                type="primary"
                onClick={() => act("issue")}
                loading={loading}
              >
                Issue
              </Button>
            )}
            {canWrite && bn.status !== "CANCELLED" && (
              <Button danger onClick={() => act("cancel")} loading={loading}>
                Cancel
              </Button>
            )}
            <Button onClick={() => window.print()}>Print</Button>
          </Space>
        }
      />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Transport record"
        description="A boat note records which consignment travelled on which boat. It moves no stock of its own — that happened when each issue note was dispatched — and the site still receives against each issue note."
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Boat">
            {bn.boatName || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Boat contact">
            {bn.boatContact || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Date of submission">
            {bn.submissionDate
              ? dayjs(bn.submissionDate).format(DT_FORMATS.date)
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Issued at">
            {bn.issuedAt
              ? dayjs(bn.issuedAt).format(DT_FORMATS.dateTime)
              : "Not yet issued"}
          </Descriptions.Item>
          {bn.notes && (
            <Descriptions.Item label="Notes">{bn.notes}</Descriptions.Item>
          )}
        </Descriptions>
      </Card>
      <Table
        rowKey="id"
        dataSource={lineRows}
        pagination={false}
        columns={[
          {
            title: "Issue Note",
            render: (_, r) => (
              <DocLink docType="GIN" id={r.ginId} docNo={r.gin?.docNo} />
            ),
          },
          {
            title: "Request",
            render: (_, r) => (
              <DocLink
                docType="MRQ"
                id={r.gin?.mrqId}
                docNo={r.gin?.mrq?.docNo}
              />
            ),
          },
          { title: "Code", render: (_, r) => r.item?.code },
          { title: "Item", render: (_, r) => r.item?.name },
          {
            title: "Qty",
            render: (_, r) => `${r.qty} ${r.item?.unit?.abbreviation ?? ""}`,
          },
        ]}
      />
      <DocumentPrintSheet
        title="Boat Note"
        docNo={bn.docNo}
        meta={[
          { label: "Boat", value: bn.boatName },
          { label: "Boat contact", value: bn.boatContact },
          { label: "Destination", value: fmtSite(bn.site) },
          {
            label: "Date of submission",
            value: bn.submissionDate
              ? dayjs(bn.submissionDate).format(DT_FORMATS.date)
              : "",
          },
        ]}
      >
        <Table
          rowKey="id"
          dataSource={lineRows}
          pagination={false}
          columns={[
            {
              title: "Issue Note",
              render: (_, r) => r.gin?.docNo ?? `#${r.ginId}`,
            },
            { title: "Code", render: (_, r) => r.item?.code },
            { title: "Item", render: (_, r) => r.item?.name },
            {
              title: "Qty",
              render: (_, r) => `${r.qty} ${r.item?.unit?.abbreviation ?? ""}`,
            },
          ]}
        />
      </DocumentPrintSheet>
    </div>
  );
}
